import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { TooltipHost, Icon } from 'office-ui-fabric-react';
import { HearingInfoBox } from '../HearingInfoBox';
import { CreateHearingWriteButtons } from './createHearingWrite/CreateHearingWriteButtons';
import { useDispatch, useSelector } from 'react-redux';
import { RenderHearing } from '../RenderHearing';
import { setPageStep } from '../../../../features/pageFeatures/createHearingPageSlice';
import {
  isUpdatingHearingSelector,
  updateFieldDataInitialize,
  autoUpdateFieldData,
} from '../../../../features/componentFeatures/renderHearingSlice';
import { getAttr, getEntityId } from '../../../../features/helpers/apiHelpers';
import {
  FIELDTYPE_CONCLUSION,
  HEARINGSTATUS_ACTIVE,
  HEARINGSTATUS_AWAITING_STARTDATE,
  HEARINGSTATUS_CONCLUDED,
  HEARINGSTATUS_DRAFT,
} from '../../../../constants/api';
import { hearingStatusByHearingIdSelector } from '../../../../features/selectors/compositeSelectors';
import { CreateHearingPreview } from './createHearingWrite/CreateHearingPreview';

const CreateHearingWrite = ({ hearingModel }) => {
  const { t: translator } = useTranslation();
  const dispatch = useDispatch();

  const hearingId = getEntityId(hearingModel);

  const hearingStatus = useSelector(
    hearingStatusByHearingIdSelector(hearingId),
  );

  const status = getAttr(hearingStatus, 'status');
  const disablePublishButton = status === HEARINGSTATUS_CONCLUDED;
  const disableSaveAsDraftButton = status !== HEARINGSTATUS_DRAFT;
  const isUpdatingHearing = useSelector(isUpdatingHearingSelector);

  const [showPreview, setShowPreview] = useState(false);

  useEffect(() => {
    dispatch(
      autoUpdateFieldData({
        hearingId,
        newStatus: status,
      }),
    );
    return function cleanup() {
      dispatch(
        autoUpdateFieldData({
          cancelAutoUpdate: true,
        }),
      );
    };
  }, [dispatch, hearingId, status]);

  const onClickPublish = () => {
    const isStatusDraft = status === HEARINGSTATUS_DRAFT;
    const isStatusActive = status === HEARINGSTATUS_ACTIVE;

    dispatch(
      updateFieldDataInitialize({
        hearingId,
        newStatus: isStatusDraft ? HEARINGSTATUS_AWAITING_STARTDATE : status,
        confirmNotifyChanges: isStatusActive,
      }),
    );
  };

  const onClickDraft = () =>
    dispatch(
      updateFieldDataInitialize({
        hearingId,
        newStatus: HEARINGSTATUS_DRAFT,
        confirmChanges: false,
      }),
    );

  const onClickPreview = () => setShowPreview(true);
  const onClosePreview = () => setShowPreview(false);
  const onClickGoBack = () => dispatch(setPageStep(1));

  return (
    <div className="create-hearing-write">
      <div className="create-form-hearing-simple" id="create-hearing-part-2">
        {!showPreview && (
          <div className="create-hearing-sub-header">
            <h3 className="p-style">
              {translator('pages.createHearing.hearingTemplate')}
            </h3>
            <TooltipHost
              content={translator('pages.createHearing.hearingTemplateTooltip')}
              styles={{ root: { marginLeft: 10 } }}
            >
              <Icon iconName="Info" />
            </TooltipHost>
          </div>
        )}
        <RenderHearing
          hearing={hearingModel}
          isEditMode={!showPreview}
          disabledFieldTypes={FIELDTYPE_CONCLUSION}
        />
        <CreateHearingWriteButtons
          onClickPublish={onClickPublish}
          onClickDraft={onClickDraft}
          onClickPreview={onClickPreview}
          onClickGoBack={onClickGoBack}
          isUpdatingHearing={isUpdatingHearing}
          disablePublishButton={disablePublishButton}
          disableSaveAsDraftButton={disableSaveAsDraftButton}
        />
        <CreateHearingPreview
          hearing={hearingModel}
          showPreview={showPreview}
          onClosePreview={onClosePreview}
          closeButtonLabel={translator('ariaLabel.closePreview')}
        />
      </div>
      <div>
        <HearingInfoBox hearing={hearingModel} disableTitle showEmptyValues />
      </div>
    </div>
  );
};

export { CreateHearingWrite };
